// src/components/settings/RequestWidgetTab.jsx
import React, { useEffect, useMemo, useState } from 'react';

/**
 * Base URL resolution (NO env change required)
 *
 * Problem you hit:
 * - If REACT_APP_API_BASE_URL = "http://localhost:5000/api"
 * - and you call "/api/settings/request-widget"
 * => final becomes "/api/api/settings/request-widget" (404)
 *
 * Fix:
 * - Normalize base by stripping trailing slashes AND a trailing "/api"
 */

// CRA env
const RAW_API_BASE = (process.env.REACT_APP_API_BASE_URL || '').trim();

const API_ORIGIN = (() => {
  // If env provided, normalize:
  // - remove trailing slashes
  // - remove trailing "/api"
  if (RAW_API_BASE) {
    return RAW_API_BASE.replace(/\/+$/, '').replace(/\/api$/, '');
  }

  // No env? In dev on localhost, default backend is usually :5000
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:5000';
  }

  // Prod / same-origin
  return '';
})();

function buildUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return API_ORIGIN ? `${API_ORIGIN}${p}` : p;
}

function clsx(...a) {
  return a.filter(Boolean).join(' ');
}

function makeLocalId() {
  return `svc_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * ✅ IMPORTANT (minimal required change):
 * Public widget controller filters services without an `id`.
 * So we must ensure every service gets a stable id.
 */
function makeServiceId() {
  // keep it short-ish, url-safe, stable
  return `svc_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`.toLowerCase();
}

function safeStr(v) {
  return v == null ? '' : String(v);
}

function cleanHexColor(v) {
  const s = safeStr(v).trim();
  if (!s) return '';
  return s.startsWith('#') ? s : `#${s}`;
}

function toNumberOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function getAuthHeaderIfAny() {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiGet(path) {
  const res = await fetch(buildUrl(path), {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...getAuthHeaderIfAny(),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

async function apiPut(path, body) {
  const res = await fetch(buildUrl(path), {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaderIfAny(),
    },
    body: JSON.stringify(body || {}),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {hint ? <div className="mt-0.5 text-xs text-slate-500">{hint}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
          checked ? 'bg-blue-600' : 'bg-slate-200'
        )}
        aria-pressed={checked}
        aria-label={label}
      >
        <span
          className={clsx(
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-5' : 'translate-x-1'
          )}
        />
      </button>
    </div>
  );
}

function CopyRow({ label, value, mono = false, onPreview }) {
  const [copied, setCopied] = useState(false);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </div>
          <div
            className={clsx(
              'mt-2 break-all text-sm text-slate-900',
              mono && 'font-mono text-[13px]'
            )}
          >
            {value || <span className="text-slate-400">—</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onPreview ? (
            <button
              type="button"
              onClick={onPreview}
              disabled={!value}
              className={clsx(
                'rounded-lg border px-3 py-1.5 text-xs font-semibold transition',
                value
                  ? 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
              )}
            >
              Preview
            </button>
          ) : null}
          <button
            type="button"
            onClick={doCopy}
            disabled={!value}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition',
              value
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
            )}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

const RequestWidgetTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [publicInfo, setPublicInfo] = useState(null);
  const [effectiveBranding, setEffectiveBranding] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [slug, setSlug] = useState('');

  const [brandingBusinessName, setBrandingBusinessName] = useState('');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('');
  const [brandingAccentColor, setBrandingAccentColor] = useState('#2563eb');

  const [intake, setIntake] = useState({
    collectAddress: false,
    collectPreferredDate: false,
    collectPhotos: false,
    collectNotes: true,
    collectEmail: true,
    collectPhone: true,
  });

  const [services, setServices] = useState([]);

  const stablePreviewUrl = useMemo(
    () => publicInfo?.stablePublicUrl || publicInfo?.publicUrl || '',
    [publicInfo]
  );

  const load = async () => {
    setLoading(true);
    setError('');
    setNotice('');

    try {
      // one-time debug to verify the base being used
      if (typeof window !== 'undefined' && !window.__RW_DEBUG_LOGGED__) {
        window.__RW_DEBUG_LOGGED__ = true;
        // eslint-disable-next-line no-console
        console.log(
          '[RequestWidgetTab] API_ORIGIN=',
          API_ORIGIN,
          'exampleUrl=',
          buildUrl('/api/settings/request-widget')
        );
      }

      const data = await apiGet('/api/settings/request-widget');

      const ws = data?.widgetSettings || {};
      const br = ws?.branding || {};
      const intakeFields = ws?.intakeFields || {};
      const svc = Array.isArray(ws?.services) ? ws.services : [];

      setEnabled(!!ws.enabled);
      setSlug(safeStr(ws.slug || ''));

      setBrandingBusinessName(safeStr(br.businessName || ''));
      setBrandingLogoUrl(safeStr(br.logoUrl || ''));
      setBrandingAccentColor(
        cleanHexColor(br.accentColor || data?.effectiveBranding?.accentColor || '#2563eb')
      );

      setIntake({
        collectAddress: !!intakeFields.collectAddress,
        collectPreferredDate: !!intakeFields.collectPreferredDate,
        collectPhotos: !!intakeFields.collectPhotos,
        collectNotes:
          intakeFields.collectNotes !== undefined ? !!intakeFields.collectNotes : true,
        collectEmail:
          intakeFields.collectEmail !== undefined ? !!intakeFields.collectEmail : true,
        collectPhone:
          intakeFields.collectPhone !== undefined ? !!intakeFields.collectPhone : true,
      });

      /**
       * ✅ Minimal required change:
       * Ensure each service has an `id` (public widget needs it).
       * If missing, generate one and keep it in state; it will be persisted on Save.
       */
      setServices(
        svc.map((s) => ({
          _localId: makeLocalId(),
          id: safeStr(s.id || '') || makeServiceId(),
          name: safeStr(s.name || ''),
          description: safeStr(s.description || ''),
          priceType: safeStr(s.priceType || 'estimate'),
          priceAmount: s.priceAmount ?? null,
          durationMinutes: s.durationMinutes ?? null,
          active: s.active !== false,
        }))
      );

      setPublicInfo(data?.public || null);
      setEffectiveBranding(data?.effectiveBranding || null);

      if (data?.notice) setNotice(String(data.notice));
    } catch (e) {
      setError(e?.message || 'Failed to load widget settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddService = () => {
    if (services.length >= 50) {
      setError('Max 50 services allowed.');
      return;
    }
    setError('');
    setServices((prev) => [
      ...prev,
      {
        _localId: makeLocalId(),
        id: makeServiceId(), // ✅ required
        name: '',
        description: '',
        priceType: 'estimate',
        priceAmount: null,
        durationMinutes: null,
        active: true,
      },
    ]);
  };

  const onUpdateService = (localId, patch) => {
    setServices((prev) => prev.map((s) => (s._localId === localId ? { ...s, ...patch } : s)));
  };

  const onRemoveService = (localId) => {
    setServices((prev) => prev.filter((s) => s._localId !== localId));
  };

  const validateBeforeSave = () => {
    const s = slug.trim();
    if (!s) return 'Slug is required.';
    if (!/^[a-z0-9-]+$/.test(s)) {
      return 'Slug can only contain lowercase letters, numbers, and dashes.';
    }

    for (const svc of services) {
      if (!svc.id || !String(svc.id).trim()) return 'Each service must have an id (auto-generated).';
      if (!svc.name.trim()) return 'Each service must have a name.';
      if (!['fixed', 'estimate', 'hourly'].includes(svc.priceType)) {
        return 'Invalid service price type.';
      }
      if (
        (svc.priceType === 'fixed' || svc.priceType === 'hourly') &&
        (svc.priceAmount == null || svc.priceAmount < 0)
      ) {
        return 'Fixed/Hourly services must have a valid price amount.';
      }
      if (svc.durationMinutes != null && (svc.durationMinutes < 0 || svc.durationMinutes > 1440)) {
        return 'Service duration must be between 0 and 1440 minutes.';
      }
    }
    return '';
  };

  const onSave = async () => {
    setError('');
    setNotice('');

    const v = validateBeforeSave();
    if (v) {
      setError(v);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        enabled,
        slug: slug.trim(),
        branding: {
          businessName: brandingBusinessName.trim() || null,
          logoUrl: brandingLogoUrl.trim() || null,
          accentColor: brandingAccentColor ? cleanHexColor(brandingAccentColor) : null,
        },
        intakeFields: { ...intake },
        services: services.map((s) => ({
          // ✅ ALWAYS send id so public widget can see services
          id: String(s.id || '').trim() || makeServiceId(),
          name: s.name.trim(),
          description: s.description.trim(),
          priceType: s.priceType,
          priceAmount:
            s.priceType === 'fixed' || s.priceType === 'hourly'
              ? Number(s.priceAmount || 0)
              : s.priceAmount == null
              ? null
              : Number(s.priceAmount),
          durationMinutes: s.durationMinutes == null ? null : Number(s.durationMinutes),
          active: !!s.active,
        })),
      };

      const data = await apiPut('/api/settings/request-widget', payload);

      setPublicInfo(data?.public || null);
      setEffectiveBranding(data?.effectiveBranding || null);
      if (data?.notice) setNotice(String(data.notice));
      else setNotice('Saved.');

      const ws = data?.widgetSettings || {};
      const svc = Array.isArray(ws?.services) ? ws.services : [];
      setServices(
        svc.map((s) => ({
          _localId: makeLocalId(),
          id: safeStr(s.id || '') || makeServiceId(), // ✅ keep safe
          name: safeStr(s.name || ''),
          description: safeStr(s.description || ''),
          priceType: safeStr(s.priceType || 'estimate'),
          priceAmount: s.priceAmount ?? null,
          durationMinutes: s.durationMinutes ?? null,
          active: s.active !== false,
        }))
      );

      setEnabled(!!ws.enabled);
      setSlug(safeStr(ws.slug || payload.slug));

      const br = ws?.branding || payload.branding || {};
      setBrandingBusinessName(safeStr(br.businessName || ''));
      setBrandingLogoUrl(safeStr(br.logoUrl || ''));
      setBrandingAccentColor(
        cleanHexColor(br.accentColor || data?.effectiveBranding?.accentColor || '#2563eb')
      );

      const intakeFields = ws?.intakeFields || payload.intakeFields || {};
      setIntake({
        collectAddress: !!intakeFields.collectAddress,
        collectPreferredDate: !!intakeFields.collectPreferredDate,
        collectPhotos: !!intakeFields.collectPhotos,
        collectNotes:
          intakeFields.collectNotes !== undefined ? !!intakeFields.collectNotes : true,
        collectEmail:
          intakeFields.collectEmail !== undefined ? !!intakeFields.collectEmail : true,
        collectPhone:
          intakeFields.collectPhone !== undefined ? !!intakeFields.collectPhone : true,
      });
    } catch (e) {
      if (e?.status === 409) {
        setError(e?.data?.message || 'That slug is already in use. Please choose another one.');
      } else {
        setError(e?.message || 'Failed to save settings.');
      }
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    if (!stablePreviewUrl) return;
    window.open(stablePreviewUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Request Widget</h2>
          <p className="mt-1 text-sm text-slate-600">
            Turn on a public request form you can embed on your website.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading || saving}
            className={clsx(
              'rounded-xl border px-4 py-2 text-sm font-semibold transition',
              loading || saving
                ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            )}
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={loading || saving}
            className={clsx(
              'rounded-xl px-4 py-2 text-sm font-semibold transition',
              loading || saving
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-slate-900 text-white hover:bg-slate-800'
            )}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="h-5 w-40 animate-pulse rounded bg-slate-100" />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
            <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
          </div>
          <div className="mt-4 h-40 animate-pulse rounded-xl bg-slate-100" />
        </div>
      ) : (
        <>
          {/* Card: Enable + Slug */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">Status</div>
                <div className="mt-1 text-sm text-slate-600">
                  Enable the widget to accept new requests from your public link.
                </div>

                <div className="mt-5">
                  <Toggle
                    checked={enabled}
                    onChange={setEnabled}
                    label={enabled ? 'Enabled' : 'Disabled'}
                    hint="When disabled, the public widget config will not load (customers won’t see the form)."
                  />
                </div>

                <div className="mt-6 grid gap-2">
                  <label className="text-sm font-semibold text-slate-900">Public slug</label>
                  <div className="text-xs text-slate-500">
                    Used for the human-friendly link. Keep it short and readable.
                  </div>
                  <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase())}
                    placeholder="e.g. sunny-handyman"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                  />
                  <div className="text-xs text-slate-500">
                    Allowed: lowercase letters, numbers, dashes.
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-[420px]">
                <div className="text-sm font-bold text-slate-900">Links & Embed</div>
                <div className="mt-1 text-sm text-slate-600">
                  Use the stable link for embeds so slug changes don’t break your website.
                </div>

                <div className="mt-4 space-y-3">
                  <CopyRow
                    label="Stable public link"
                    value={publicInfo?.stablePublicUrl || ''}
                    onPreview={openPreview}
                  />
                  <CopyRow
                    label="Slug public link"
                    value={publicInfo?.publicUrl || ''}
                    onPreview={() => {
                      const u = publicInfo?.publicUrl;
                      if (u) window.open(u, '_blank', 'noopener,noreferrer');
                    }}
                  />
                  <CopyRow
                    label="Iframe embed snippet (recommended)"
                    value={publicInfo?.embedCode || ''}
                    mono
                    onPreview={openPreview}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card: Branding */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-bold text-slate-900">Branding</div>
                <div className="mt-1 text-sm text-slate-600">
                  Customize what customers see on the public request form.
                </div>
              </div>
              {effectiveBranding ? (
                <div className="hidden sm:block rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                  <div className="font-semibold text-slate-700">Effective</div>
                  <div className="mt-1">
                    <span className="font-semibold">Name:</span>{' '}
                    {effectiveBranding.businessName || '—'}
                  </div>
                  <div className="mt-0.5">
                    <span className="font-semibold">Accent:</span>{' '}
                    {effectiveBranding.accentColor || '—'}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <div>
                <label className="text-sm font-semibold text-slate-900">Business name</label>
                <input
                  value={brandingBusinessName}
                  onChange={(e) => setBrandingBusinessName(e.target.value)}
                  placeholder="e.g. Sunny Handyman Services"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                />
                <div className="mt-1 text-xs text-slate-500">
                  Leave empty to use your company name.
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-900">Logo URL</label>
                <input
                  value={brandingLogoUrl}
                  onChange={(e) => setBrandingLogoUrl(e.target.value)}
                  placeholder="https://…"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                />
                <div className="mt-1 text-xs text-slate-500">
                  Must be a publicly accessible image URL.
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-900">Accent color</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={cleanHexColor(brandingAccentColor) || '#2563eb'}
                    onChange={(e) => setBrandingAccentColor(e.target.value)}
                    className="h-10 w-12 rounded-lg border border-slate-200 bg-white p-1"
                    aria-label="Accent color picker"
                  />
                  <input
                    value={brandingAccentColor}
                    onChange={(e) => setBrandingAccentColor(e.target.value)}
                    placeholder="#2563eb"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                  />
                </div>
                <div className="mt-1 text-xs text-slate-500">Used for buttons and highlights.</div>
              </div>
            </div>
          </div>

          {/* Card: Intake fields */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div>
              <div className="text-sm font-bold text-slate-900">Intake fields</div>
              <div className="mt-1 text-sm text-slate-600">
                Choose what information customers can submit.
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <Toggle
                  checked={intake.collectEmail}
                  onChange={(v) => setIntake((p) => ({ ...p, collectEmail: v }))}
                  label="Collect email"
                  hint="Recommended for follow-ups."
                />
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <Toggle
                  checked={intake.collectPhone}
                  onChange={(v) => setIntake((p) => ({ ...p, collectPhone: v }))}
                  label="Collect phone"
                  hint="Recommended for fast contact."
                />
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <Toggle
                  checked={intake.collectAddress}
                  onChange={(v) => setIntake((p) => ({ ...p, collectAddress: v }))}
                  label="Collect address"
                  hint="Useful for on-site jobs."
                />
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <Toggle
                  checked={intake.collectPreferredDate}
                  onChange={(v) => setIntake((p) => ({ ...p, collectPreferredDate: v }))}
                  label="Collect preferred date"
                  hint="Ask when they want the service."
                />
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <Toggle
                  checked={intake.collectNotes}
                  onChange={(v) => setIntake((p) => ({ ...p, collectNotes: v }))}
                  label="Collect notes"
                  hint="Let customers add extra context."
                />
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <Toggle
                  checked={intake.collectPhotos}
                  onChange={(v) => setIntake((p) => ({ ...p, collectPhotos: v }))}
                  label="Collect photos"
                  hint="Optional (requires public page UI support)."
                />
              </div>

              <div className="lg:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Tip: If you turn off both email and phone, your public submit controller may reject
                submissions (because it needs at least one contact method). Keep at least one ON.
              </div>
            </div>
          </div>

          {/* Card: Services */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-bold text-slate-900">Services</div>
                <div className="mt-1 text-sm text-slate-600">
                  Customers pick a service first. You can disable services anytime.
                </div>
              </div>

              <button
                type="button"
                onClick={onAddService}
                className="w-full sm:w-auto rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition"
              >
                Add service
              </button>
            </div>

            <div className="mt-5 space-y-4">
              {services.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                  No services yet. Add your first service.
                </div>
              ) : null}

              {services.map((svc, idx) => {
                const isEstimate = svc.priceType === 'estimate';
                const isFixedOrHourly = svc.priceType === 'fixed' || svc.priceType === 'hourly';

                return (
                  <div key={svc._localId} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-bold text-slate-900">Service {idx + 1}</div>
                        {!svc.active ? (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                            Disabled
                          </span>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onUpdateService(svc._localId, { active: !svc.active })}
                          className={clsx(
                            'rounded-xl px-3 py-1.5 text-xs font-semibold transition',
                            svc.active
                              ? 'bg-slate-900 text-white hover:bg-slate-800'
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          )}
                        >
                          {svc.active ? 'Disable' : 'Enable'}
                        </button>

                        <button
                          type="button"
                          onClick={() => onRemoveService(svc._localId)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
                          title="Remove from list (will be saved on next Save Changes)"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-4 lg:grid-cols-2">
                      <div>
                        <label className="text-sm font-semibold text-slate-900">Name</label>
                        <input
                          value={svc.name}
                          onChange={(e) => onUpdateService(svc._localId, { name: e.target.value })}
                          placeholder="e.g. Deep Cleaning"
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-slate-900">Price type</label>
                        <select
                          value={svc.priceType}
                          onChange={(e) => {
                            const next = e.target.value;
                            onUpdateService(svc._localId, {
                              priceType: next,
                              priceAmount: next === 'estimate' ? null : svc.priceAmount ?? 0,
                            });
                          }}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                        >
                          <option value="estimate">Estimate</option>
                          <option value="fixed">Fixed</option>
                          <option value="hourly">Hourly</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-slate-900">Price amount</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          value={svc.priceAmount ?? ''}
                          onChange={(e) =>
                            onUpdateService(svc._localId, { priceAmount: toNumberOrNull(e.target.value) })
                          }
                          disabled={isEstimate}
                          placeholder={isEstimate ? 'Not required' : 'e.g. 120'}
                          className={clsx(
                            'mt-1 w-full rounded-xl border px-4 py-2 text-sm outline-none transition',
                            isEstimate
                              ? 'border-slate-200 bg-slate-50 text-slate-400'
                              : 'border-slate-200 bg-white text-slate-900 focus:border-blue-500'
                          )}
                        />
                        <div className="mt-1 text-xs text-slate-500">
                          {isFixedOrHourly ? 'Required for fixed/hourly.' : 'Optional for estimate.'}
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-slate-900">Duration (minutes)</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          max="1440"
                          step="5"
                          value={svc.durationMinutes ?? ''}
                          onChange={(e) =>
                            onUpdateService(svc._localId, { durationMinutes: toNumberOrNull(e.target.value) })
                          }
                          placeholder="e.g. 60"
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                        />
                        <div className="mt-1 text-xs text-slate-500">
                          Optional — helps customers set expectations.
                        </div>
                      </div>

                      <div className="lg:col-span-2">
                        <label className="text-sm font-semibold text-slate-900">Description</label>
                        <textarea
                          value={svc.description}
                          onChange={(e) => onUpdateService(svc._localId, { description: e.target.value })}
                          placeholder="Short description shown to customers."
                          rows={3}
                          className="mt-1 w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                        />
                        <div className="mt-1 text-xs text-slate-500">Keep it under ~2–3 lines.</div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {stablePreviewUrl ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-bold text-slate-900">Preview</div>
                      <div className="mt-1 text-sm text-slate-600">
                        Open the public page to see the form customers will use.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={openPreview}
                      className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition"
                    >
                      Open preview
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-bold text-slate-900">Embed notes</div>
            <div className="mt-1 text-sm text-slate-600">
              If the iframe shows blank on a customer website, it’s almost always a hosting header
              issue (CSP / frame-ancestors / X-Frame-Options). Your public page must be embeddable.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RequestWidgetTab;
